import React, { useRef, useLayoutEffect } from 'react';
import DynamicNoteCard from './DynamicNoteCard';

// Textarea som växer automatiskt med innehållet
const AutoTextarea = ({ value, onChange, onBlur, className }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      rows={2}
      className={className}
      style={{ resize: 'none', overflow: 'hidden' }}
    />
  );
};

const STANDARD_BADGES = [
  'Projektering', 'Våtrum', 'Kök', 'Bygg', 'Måleri',
  'Golv', 'El', 'VVS', 'Tillbyggnad', 'Källare', 'Utomhus'
];

const AnteckningarSection = ({
  intervjuFrågor,
  dynamicNotes,
  focusNoteId,
  expanded,
  onToggle,
  onChange,
  onAddNote,
  onUpdateNoteTitle,
  onUpdateNoteContent,
  onSaveNote,
  onDeleteNote,
  onToggleNoteEdit,
  onFocusCleared,
  onBlur,
  onProofread
}) => {
  return (
    <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Header */}
      <div
        className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-leadme-gray-dark">Anteckningar</h2>
          <div className="flex items-center gap-3">
            {onProofread && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onProofread(); }}
                className="px-2.5 py-1 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-md flex items-center gap-1.5"
                title="Korrekturläs anteckningar (formalia)"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Korrekturläs
              </button>
            )}
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 sm:p-6">
          {/* Knappar för att lägga till anteckningar */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => onAddNote()}
                className="px-3 py-1 bg-leadme-pink text-white text-xs font-medium rounded-full hover:bg-leadme-pink-dark transition-colors border border-pink-200"
              >
                + Ny anteckning
              </button>

              {/* Badge-knappar för fördefinierade ämnen */}
              {STANDARD_BADGES.map(badge => (
                <button
                  key={badge}
                  onClick={() => onAddNote(badge)}
                  className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full hover:bg-slate-200 transition-colors border border-slate-200"
                >
                  + {badge}
                </button>
              ))}
            </div>
          </div>

          {/* Grid layout för de 7 frågorna - 3 kolumner på stora skärmar, responsivt */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                1. Hur hörde du talas om oss?
              </label>
              <AutoTextarea
                value={intervjuFrågor.talasOmOss}
                onChange={(e) => onChange('talasOmOss', e.target.value)}
                onBlur={onBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                2. Tidigare erfarenhet?
              </label>
              <AutoTextarea
                value={intervjuFrågor.tidigareErfarenhet}
                onChange={(e) => onChange('tidigareErfarenhet', e.target.value)}
                onBlur={onBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                3. Varför ska ni genomföra projektet?
              </label>
              <AutoTextarea
                value={intervjuFrågor.genomförandeTid}
                onChange={(e) => onChange('genomförandeTid', e.target.value)}
                onBlur={onBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                4. Vilka ska bo i bostaden?
              </label>
              <AutoTextarea
                value={intervjuFrågor.vemBoIBostaden}
                onChange={(e) => onChange('vemBoIBostaden', e.target.value)}
                onBlur={onBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                5. Vad är dina förväntningar på oss?
              </label>
              <AutoTextarea
                value={intervjuFrågor.förväntningar}
                onChange={(e) => onChange('förväntningar', e.target.value)}
                onBlur={onBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                6. Hur kommer ni att bo under renoveringen?
              </label>
              <AutoTextarea
                value={intervjuFrågor.kommunikation}
                onChange={(e) => onChange('kommunikation', e.target.value)}
                onBlur={onBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                7. Vilka parkeringsmöjligheter finns det?
              </label>
              <AutoTextarea
                value={intervjuFrågor.parkeringsmöjligheter}
                onChange={(e) => onChange('parkeringsmöjligheter', e.target.value)}
                onBlur={onBlur}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink text-sm"
              />
            </div>
          </div>

          {/* Dynamiska anteckningar - efter standardfrågorna */}
          {dynamicNotes.length > 0 && (
            <div className="mt-6 space-y-3">
              {dynamicNotes
                .sort((a, b) => a.id - b.id)
                .map(note => (
                  <DynamicNoteCard
                    key={note.id}
                    note={note}
                    shouldFocus={focusNoteId === note.id}
                    onFocused={onFocusCleared}
                    onTitleChange={(value) => {
                      onUpdateNoteTitle(note.id, value);
                    }}
                    onTitleSubmit={() => onSaveNote(note.id)}
                    onContentChange={(value) => onUpdateNoteContent(note.id, value)}
                    onContentSave={onSaveNote}
                    onDelete={() => onDeleteNote(note.id)}
                    onToggleEdit={() => onToggleNoteEdit(note.id)}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnteckningarSection;
